'use client'

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import Cropper from 'react-easy-crop'
import type { Area, Point } from 'react-easy-crop'
import {
  RotateCw,
  RefreshCw,
  Crop,
  Sliders,
  Palette,
  Sparkles,
  Eye,
  EyeOff,
  AlertCircle,
  RefreshCcw,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog/Dialog'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { getImageUrl } from '@/lib'
import { analytics } from '@/lib/analytics'
import { showToast } from '@/components/ui/Toast'
import { logger } from '@/lib/logger'
import type { StoredImage, ImageContext, AspectRatio } from '@/types/image'
import {
  CONTEXT_ASPECT_RATIOS,
  CONTEXT_LABELS,
  getAspectRatioValue,
} from '@/types/image'
import { FilterControls } from './editor/FilterControls'
import { PresetFilters } from './editor/PresetFilters'
import { OverlayControls } from './editor/OverlayControls'
import {
  type FilterSettings,
  type FilterPreset,
  type OverlayType,
  DEFAULT_FILTERS,
  filterSettingsToCSS,
  applyPreset,
  getOverlayConfig,
} from './editor/filterPresets'
import { exportEditedImage, blobToFile } from './editor/canvasUtils'

interface ImageEditorModalProps {
  isOpen: boolean
  onClose: () => void
  image: StoredImage
  /** Determines the default suggested aspect ratio */
  context: ImageContext
  onSave: (editedImage: StoredImage, file: File) => void
  isSaving?: boolean
  /** Locks the crop to a single aspect ratio */
  lockedAspectRatio?: AspectRatio
  /** When set, only these ratios are offered (e.g. portrait + landscape for portrait_or_landscape) */
  allowedAspectRatios?: AspectRatio[]
  contextLabel?: string
}

type EditorTab = 'crop' | 'filters' | 'presets' | 'overlay'

const TABS: { id: EditorTab; label: string; icon: typeof Crop }[] = [
  { id: 'crop', label: 'Crop', icon: Crop },
  { id: 'presets', label: 'Presets', icon: Sparkles },
  { id: 'overlay', label: 'Overlay', icon: Palette },
  { id: 'filters', label: 'Adjust', icon: Sliders },
]

const ASPECT_RATIO_OPTIONS: { value: AspectRatio; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: '1:1', label: '1:1' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
  { value: '4:6', label: '4:6' },
  { value: '6:4', label: '6:4' },
  { value: '5:7', label: '5:7' },
  { value: '7:5', label: '7:5' },
  { value: '4:5', label: '4:5' },
  { value: '5:4', label: '5:4' },
]

/**
 * Full-featured image editor: crop, rotate, zoom, filter adjustments,
 * preset filters, and overlay effects (vignette, gradients).
 */
export function ImageEditorModal({
  isOpen,
  onClose,
  image,
  context,
  onSave,
  isSaving = false,
  lockedAspectRatio,
  allowedAspectRatios,
  contextLabel: contextLabelProp,
}: ImageEditorModalProps) {
  // When allowedAspectRatios is set (e.g. portrait_or_landscape), default to the first allowed ratio
  const suggestedAspectRatio = allowedAspectRatios?.length
    ? allowedAspectRatios[0]
    : (lockedAspectRatio ?? CONTEXT_ASPECT_RATIOS[context])
  const contextLabel = contextLabelProp ?? CONTEXT_LABELS[context]

  const isAspectRatioLocked =
    lockedAspectRatio !== undefined ||
    (allowedAspectRatios?.length ?? 0) > 0 ||
    suggestedAspectRatio !== 'free'

  // Narrows the ratio picker to only the ratios valid for this context
  const allowedRatios = useMemo(() => {
    if (!isAspectRatioLocked || suggestedAspectRatio === 'free') {
      return ASPECT_RATIO_OPTIONS
    }
    if (allowedAspectRatios?.length) {
      const set = new Set(allowedAspectRatios)
      return ASPECT_RATIO_OPTIONS.filter((opt) => set.has(opt.value))
    }
    return ASPECT_RATIO_OPTIONS.filter(
      (opt) => opt.value === suggestedAspectRatio
    )
  }, [isAspectRatioLocked, suggestedAspectRatio, allowedAspectRatios])

  const [activeTab, setActiveTab] = useState<EditorTab>('crop')
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [aspectRatio, setAspectRatio] =
    useState<AspectRatio>(suggestedAspectRatio)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [filters, setFilters] = useState<FilterSettings>(DEFAULT_FILTERS)
  const [activePreset, setActivePreset] = useState<string | null>('original')
  const [overlay, setOverlay] = useState<OverlayType>('none')
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [imageLoadState, setImageLoadState] = useState<
    'loading' | 'loaded' | 'error'
  >('loading')
  const [imageLoadError, setImageLoadError] = useState<string | null>(null)

  // Refs prevent duplicate analytics events across re-renders
  const hasTrackedOpen = useRef(false)
  const editsTracked = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true
      analytics.imageEditor.opened(image.id)
    }
    if (!isOpen) {
      hasTrackedOpen.current = false
      editsTracked.current = new Set()
    }
  }, [isOpen, image.id])

  // filterCSS is applied live to the Cropper containerStyle so changes are visible in real time
  const filterCSS = useMemo(() => filterSettingsToCSS(filters), [filters])
  const overlayConfig = useMemo(() => getOverlayConfig(overlay), [overlay])

  const imageUrl = getImageUrl(image.imageUrl, 'original')

  // Preload image manually to track load/error state before showing the Cropper
  useEffect(() => {
    if (!isOpen || !imageUrl) {
      setImageLoadState('loading')
      setImageLoadError(null)
      return
    }

    setImageLoadState('loading')
    setImageLoadError(null)

    const img = new window.Image()
    img.crossOrigin = 'anonymous'

    const handleLoad = () => {
      setImageLoadState('loaded')
      setImageLoadError(null)
    }
    const handleError = () => {
      setImageLoadState('error')
      setImageLoadError(
        'Failed to load image. This may be due to network issues or the image is no longer available.'
      )
      logger.error('Failed to load image for editor:', imageUrl)
    }

    img.addEventListener('load', handleLoad)
    img.addEventListener('error', handleError)
    img.src = imageUrl

    return () => {
      img.removeEventListener('load', handleLoad)
      img.removeEventListener('error', handleError)
    }
  }, [isOpen, imageUrl])

  const handleRetryLoad = useCallback(() => {
    setImageLoadState('loading')
    setImageLoadError(null)

    const img = new window.Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      setImageLoadState('loaded')
      setImageLoadError(null)
    }
    img.onerror = () => {
      setImageLoadState('error')
      setImageLoadError(
        'Failed to load image. Please check your connection and try again.'
      )
    }

    // Cache buster forces a fresh fetch on retry
    const sep = imageUrl.includes('?') ? '&' : '?'
    img.src = `${imageUrl}${sep}retry=${Date.now()}`
  }, [imageUrl])

  // Preview renders the final canvas output (crop + filters + overlay) into a blob URL
  useEffect(() => {
    if (!isPreviewMode || !croppedAreaPixels) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }
      return
    }

    let cancelled = false

    const generatePreview = async () => {
      setIsGeneratingPreview(true)
      try {
        const blob = await exportEditedImage(imageUrl, {
          cropArea: croppedAreaPixels,
          rotation,
          zoom,
          filters,
          overlay,
        })
        if (!cancelled) {
          if (previewUrl) URL.revokeObjectURL(previewUrl)
          setPreviewUrl(URL.createObjectURL(blob))
        }
      } catch (error) {
        logger.error('Failed to generate preview:', error)
      } finally {
        if (!cancelled) setIsGeneratingPreview(false)
      }
    }

    generatePreview()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewMode, croppedAreaPixels, rotation, filters, overlay, imageUrl])

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    []
  )

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360)
    if (!editsTracked.current.has('rotate')) {
      editsTracked.current.add('rotate')
    }
  }, [])

  const handleReset = useCallback(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    setAspectRatio(suggestedAspectRatio)
    setFilters(DEFAULT_FILTERS)
    setActivePreset('original')
    setOverlay('none')
  }, [suggestedAspectRatio])

  const handlePresetSelect = useCallback((preset: FilterPreset) => {
    setFilters(applyPreset(preset))
    setActivePreset(preset.id)
    if (preset.overlay) setOverlay(preset.overlay)
    if (!editsTracked.current.has('filter')) {
      editsTracked.current.add('filter')
      analytics.imageEditor.filterApplied(preset.id)
    }
  }, [])

  const handlePresetClear = useCallback(() => {
    setActivePreset(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) {
      showToast('warning', 'Please wait for the image to load before saving.')
      return
    }

    try {
      const blob = await exportEditedImage(imageUrl, {
        cropArea: croppedAreaPixels,
        rotation,
        zoom,
        filters,
        overlay,
      })

      const filename = `edited-${image.filename || 'image'}.jpg`
      const file = blobToFile(blob, filename)

      const editedImage: StoredImage = {
        ...image,
        id: `edited-${image.id}-${Date.now()}`,
        type: 'edit',
        isEdited: true,
        originalImageId: image.originalImageId || image.id,
        createdAt: new Date().toISOString(),
        editData: {
          cropData: {
            x: croppedAreaPixels.x,
            y: croppedAreaPixels.y,
            width: croppedAreaPixels.width,
            height: croppedAreaPixels.height,
          },
          rotation,
          zoom,
        },
      }

      // 'crop' is always included since croppedAreaPixels is present
      const editsMade = ['crop', ...Array.from(editsTracked.current)]
      analytics.imageEditor.saved(image.id, editsMade)

      onSave(editedImage, file)
    } catch (error) {
      logger.error('Failed to export edited image:', error)
      showToast(
        'error',
        'Failed to save image. This may be due to image loading restrictions. Please try again.'
      )
    }
  }, [
    croppedAreaPixels,
    imageUrl,
    rotation,
    zoom,
    filters,
    overlay,
    image,
    onSave,
  ])

  if (!isOpen) return null

  const isEditorDisabled =
    !croppedAreaPixels || isSaving || imageLoadState !== 'loaded'

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!w-[98vw] !max-w-[1800px] !h-[95vh] !max-h-[950px] !inset-auto !left-[50%] !top-[50%] !-translate-x-1/2 !-translate-y-1/2 flex flex-col !p-0 !gap-0 !rounded-xl">
        {isSaving && (
          <div className="absolute inset-0 z-50 rounded-xl overflow-hidden flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4">
              <div
                className="border-4 border-primary border-t-transparent rounded-full animate-spin"
                style={{ width: 48, height: 48 }}
              />
              <p className="text-white text-lg font-medium drop-shadow-lg">
                Saving image...
              </p>
            </div>
          </div>
        )}

        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="text-lg sm:text-xl">
            Image Editor
            <span className="text-muted-foreground font-normal">
              {' '}
              — {contextLabel}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Edit crop, rotation, filters, and overlays for this image.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
          {/* Canvas area */}
          <div className="relative h-[200px] sm:h-[300px] lg:h-auto lg:flex-1 shrink-0 lg:shrink bg-black">
            {isPreviewMode ? (
              <div className="absolute inset-2 sm:inset-4 rounded-lg overflow-hidden flex items-center justify-center bg-neutral-900">
                {isGeneratingPreview ? (
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className="border-3 border-white/30 border-t-white rounded-full animate-spin"
                      style={{ width: 32, height: 32 }}
                    />
                    <p className="text-white/70 text-sm">
                      Generating preview...
                    </p>
                  </div>
                ) : previewUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element -- Blob URL preview, not optimizable
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  />
                ) : null}

                <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
                  <Eye className="w-3 h-3" />
                  Preview Mode
                </div>
              </div>
            ) : (
              <>
                {imageLoadState === 'loading' && (
                  <div className="absolute inset-2 sm:inset-4 rounded-lg overflow-hidden flex items-center justify-center bg-neutral-900">
                    <div className="flex flex-col items-center gap-4">
                      <div
                        className="border-4 border-white/20 border-t-white rounded-full animate-spin"
                        style={{ width: 48, height: 48 }}
                      />
                      <p className="text-white/70 text-sm">Loading image...</p>
                    </div>
                  </div>
                )}

                {imageLoadState === 'error' && (
                  <div className="absolute inset-2 sm:inset-4 rounded-lg overflow-hidden flex items-center justify-center bg-neutral-900">
                    <div className="flex flex-col items-center gap-4 max-w-sm text-center px-4">
                      <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                        <AlertCircle className="w-8 h-8 text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">
                          Failed to Load Image
                        </h3>
                        <p className="text-white/60 text-sm">
                          {imageLoadError ||
                            'The image could not be loaded. Please try again.'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRetryLoad}
                        className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
                      >
                        <RefreshCcw className="w-4 h-4" />
                        Retry
                      </Button>
                    </div>
                  </div>
                )}

                {imageLoadState === 'loaded' && (
                  <>
                    <div className="absolute inset-2 sm:inset-4 rounded-lg overflow-hidden">
                      <Cropper
                        image={imageUrl}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={getAspectRatioValue(aspectRatio)}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                        showGrid={activeTab === 'crop'}
                        objectFit="contain"
                        minZoom={1}
                        maxZoom={3}
                        mediaProps={{ crossOrigin: 'anonymous' }}
                        style={{ containerStyle: { filter: filterCSS } }}
                      />
                    </div>

                    {overlay !== 'none' && (
                      <div
                        className="absolute inset-2 sm:inset-4 rounded-lg pointer-events-none"
                        style={{
                          background: overlayConfig.css,
                          mixBlendMode:
                            overlayConfig.blendMode as React.CSSProperties['mixBlendMode'],
                          opacity: overlayConfig.opacity,
                        }}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Controls panel */}
          <div className="w-full lg:w-[420px] xl:w-[480px] border-t lg:border-t-0 lg:border-l flex flex-col bg-card flex-1 lg:flex-none lg:shrink-0 overflow-hidden">
            <div className="flex border-b shrink-0">
              {TABS.map((tab) => {
                const Icon = tab.icon
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium',
                      'transition-colors touch-manipulation',
                      isActive
                        ? 'text-primary border-b-2 border-primary -mb-px'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                )
              })}
            </div>

            <div className="flex-1 overflow-y-auto p-4 pb-24 sm:pb-4 min-h-0">
              {activeTab === 'crop' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRotate}
                      className="flex-1"
                    >
                      <RotateCw className="w-4 h-4 mr-1.5" />
                      Rotate 90°
                    </Button>
                  </div>

                  {/* Separated from Rotate to prevent accidental tap */}
                  <div className="flex items-center justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="text-muted-foreground hover:text-destructive text-xs"
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      Reset All
                    </Button>
                  </div>

                  {rotation > 0 && (
                    <p className="text-xs text-muted-foreground text-center">
                      Rotated: {rotation}°
                    </p>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Aspect Ratio
                      {isAspectRatioLocked && (
                        <span className="ml-2 text-xs font-normal text-amber-500">
                          (locked to {suggestedAspectRatio})
                        </span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {allowedRatios.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setAspectRatio(option.value)}
                          className={cn(
                            'px-3 py-1.5 rounded-md text-sm font-medium',
                            'transition-all duration-150 border',
                            aspectRatio === option.value
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {isAspectRatioLocked && (
                      <p className="text-xs text-muted-foreground">
                        Aspect ratio restricted for {contextLabel}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Zoom ({Math.round(zoom * 100)}%)
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.01}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <p className="text-xs text-muted-foreground">
                      Zoom in to reposition the image within the crop area
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'filters' && (
                <FilterControls
                  filters={filters}
                  onChange={setFilters}
                  onPresetClear={handlePresetClear}
                />
              )}

              {activeTab === 'presets' && (
                <PresetFilters
                  imageUrl={imageUrl}
                  activePreset={activePreset}
                  onSelect={handlePresetSelect}
                />
              )}

              {activeTab === 'overlay' && (
                <OverlayControls
                  imageUrl={imageUrl}
                  activeOverlay={overlay}
                  onSelect={setOverlay}
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 py-4 border-t bg-card shrink-0">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              disabled={isEditorDisabled}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border',
                isEditorDisabled
                  ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground border-border'
                  : isPreviewMode
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
              )}
              title={
                imageLoadState !== 'loaded'
                  ? 'Wait for image to load'
                  : isPreviewMode
                    ? 'Exit preview mode'
                    : 'Preview final result'
              }
            >
              {isGeneratingPreview ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : isPreviewMode ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Preview</span>
            </button>

            <div className="flex-1 sm:hidden" />

            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 sm:flex-none"
            >
              Close
            </Button>
            <Button
              onClick={handleSave}
              disabled={isEditorDisabled}
              loading={isSaving}
              className="flex-1 sm:flex-none"
            >
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
